import { useInfiniteQuery } from "@tanstack/react-query";

import AuditsAPI from "@/api/Audits/Audits";

export interface AuditLogUser {
  user_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  title: string | null;
}

export interface AuditLogAudit {
  uuid: string;
  name: string;
  status: string;
  created_on: string;
}

export interface AuditLog {
  id: number;
  uuid: string;
  created_on: string;
  passed_audit: boolean;
  object_uuid: string;
  // Stored lowercase — CreateAuditLogSchema validates "evidence" | "storage"
  // and getAuditLogs returns the column verbatim. The capitalized literals in
  // the server SQL only work via MySQL's case-insensitive collation.
  object_type: "evidence" | "storage";
  object_name: string | null;
  notes: string | null;
  created_by: AuditLogUser;
  audit: AuditLogAudit;
}

// Shape produced by useTableState's resolvedQuery for this table; scope is
// either an object (evidence/storage item) or a single audit. No `filter` —
// GET /audits/audit-logs has no conditions support, and no index signature so
// a typo'd scope key fails to compile instead of silently disabling the query.
export interface GetAuditLogsQuery {
  object_uuid?: string;
  audit_uuid?: string;
  pageSize?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
}

// GET /audits/audit-logs takes plain query params (no conditions envelope);
// `order` must arrive as a JSON string of {field, sort}. Sortable fields are
// whitelisted server-side: created_on, object_name, created_by, passed_audit.
interface GetAuditLogsParams {
  object_uuid?: string;
  audit_uuid?: string;
  pageSize?: number;
  search?: string;
  order?: string;
}

export interface GetAuditLogsResponse {
  data: AuditLog[];
  total: number;
  pageCount: number;
  currentPage: number;
  nextPage: number | null;
  pageSize: number;
}

// The server's sort fieldMap; any other field is silently ignored, which
// drops the ORDER BY entirely and makes LIMIT/OFFSET pages nondeterministic
// (duplicated/vanishing rows in infinite scroll AND in the export's
// server-side page loop). Sort requests outside this list — e.g. a stale
// persisted sortState from localStorage — fall back to the created_on
// default instead of reaching the server.
const SORTABLE_FIELDS = new Set([
  "created_on",
  "object_name",
  "created_by",
  "passed_audit",
]);

const resolveAuditLogsParams = (
  query: GetAuditLogsQuery
): GetAuditLogsParams => {
  const { object_uuid, audit_uuid, pageSize, search, sort_by, sort_dir } =
    query;

  const order = sort_by
    ? SORTABLE_FIELDS.has(sort_by)
      ? { field: sort_by, sort: sort_dir === "desc" ? "desc" : "asc" }
      : { field: "created_on", sort: "desc" }
    : undefined;

  return {
    object_uuid,
    audit_uuid,
    pageSize,
    search: search === "" ? undefined : search,
    order: order ? JSON.stringify(order) : undefined,
  };
};

const auditLogsQueryKeys = {
  base: ["audits:logs"],
  infiniteList: (params: GetAuditLogsParams) => [
    "audits:logs",
    "infinite",
    params,
  ],
};

const useGetInfiniteAuditLogs = (
  query: GetAuditLogsQuery,
  options?: { enabled?: boolean }
) => {
  const params = resolveAuditLogsParams(query);

  return useInfiniteQuery<GetAuditLogsResponse>({
    queryKey: auditLogsQueryKeys.infiniteList(params),
    queryFn: async ({ pageParam }) => {
      const res = await AuditsAPI.getAuditLogs({ ...params, page: pageParam });
      // The API layer never rejects: HTTP/network failures resolve as
      // { success: false, error } (connection.js handleError). Convert those
      // to real errors here — otherwise react-query records success and the
      // table renders a silent empty result set with the retry UI unreachable.
      if (!res || res.success === false || !Array.isArray(res.data)) {
        throw new Error("Failed to load audit logs");
      }
      return res;
    },
    getNextPageParam: (lastPage) => lastPage?.nextPage ?? null,
    initialPageParam: 1,
    placeholderData: (data) => data,
    enabled: options?.enabled ?? !!(params.object_uuid || params.audit_uuid),
  });
};

export const AuditLogsQuery = {
  queryKeys: auditLogsQueryKeys,
  resolveAuditLogsParams,
  useGetInfiniteAuditLogs,
};
